/**
 * Disease Cascade — build clarification intents and workflows for
 * abstract disease-level goals.
 *
 * The cascade descends the abstraction ladder from the user's initial
 * high-level prompt ("design a drug for hep B") toward a concrete plan:
 *
 *   L0 → L1: ask which drug target (mechanism)
 *   L1 → L2: pick structure source (PDB / AlphaFold / fold)
 *   L2 → workflow: build the DMTA plan
 *
 * Each stage produces either a clarification intent (asking the user
 * another question) or a final workflow plan. The clarification
 * context carries the disease slug so the resume handler knows how
 * to interpret the user's answer.
 *
 * @module gibbs/knowledge/diseaseCascade
 */

import { diseaseTargets } from './diseaseTargets';
import { buildDockAndScoreWorkflow } from './dockAndScoreShortcut';
import { buildPlanFromTemplate } from './workflowTemplateShortcut';

// ─── L0 → L1: disease to target selection ─────────────────────────────────

/**
 * Build a clarification intent asking the user which drug target to pursue.
 *
 * 2026-06-02: optional `workflowIntent` parameter threads the workflow
 * template the user named in their original message (detected via
 * `detectTemplateAlias` in workflowTemplateShortcut.js). Stored in the
 * clarification context so the resume handler can emit a multi-step plan
 * (load + dock + score + grow + …) instead of just the load step.
 *
 * @param {string} diseaseSlug
 * @param {string|null} [workflowIntent] - template id like 'hit-to-lead'
 * @returns {Object} clarify-intent object compatible with the text-to-action pipeline
 */
export function buildDiseaseTargetClarification(diseaseSlug, workflowIntent = null) {
    const entry = diseaseTargets.diseases?.[diseaseSlug];
    if (!entry) {
        return {
            intent: 'chat',
            message: `I don't have target information for "${diseaseSlug}" yet.`,
        };
    }

    const targets = entry.topTargets || [];

    if (targets.length === 0) {
        // Stub disease — tell the user honestly
        return {
            intent: 'chat',
            message: `I recognized "${entry.canonicalName}" as a drug discovery `
                + "goal, but I don't yet have a curated list of drug targets "
                + 'for it in my knowledge base. The Gibbs team is working on '
                + 'filling this in — in the meantime, if you can tell me a '
                + 'specific target protein, I can help you from there.',
        };
    }

    const question = targetSelectionQuestion(entry);

    const options = targets.map((t, idx) => ({
        label: formatTargetOptionLabel(t),
        value: {
            cascade: 'disease_target_selection',
            diseaseSlug,
            targetIdx: idx,
            ...(workflowIntent ? { workflowIntent } : {}),
        },
    }));

    // Final option: an escape hatch for users who want to pick their own
    // target not in our list
    options.push({
        label: 'None of these — I have a different target in mind',
        value: {
            cascade: 'disease_target_selection',
            diseaseSlug,
            targetIdx: -1,
            ...(workflowIntent ? { workflowIntent } : {}),
        },
    });

    return {
        intent: 'clarify',
        question,
        options,
        missing: ['target'],
        context: {
            cascade: 'disease_target_selection',
            diseaseSlug,
            stage: 'L0_to_L1',
            ...(workflowIntent ? { workflowIntent } : {}),
        },
    };
}

/**
 * @param {Object} entry
 * @returns {string}
 */
function targetSelectionQuestion(entry) {
    const name = entry.canonicalName;
    const n = (entry.topTargets || []).length;
    return `${name} has multiple drug targets. Which mechanism would you like `
        + `to pursue? (${n} options shown)`;
}

/**
 * Build a user-facing label for a target option chip.
 * Format: "Gene symbol — short name (X structures, maturity)"
 *
 * @param {Object} t - A target object from diseaseTargets
 * @returns {string}
 */
function formatTargetOptionLabel(t) {
    const structureNote = t.pdbCount > 0
        ? `${t.pdbCount} structure${t.pdbCount === 1 ? '' : 's'}`
        : 'no experimental structure';
    const shortName = t.name ? t.name.replace(/\s*\(.*?\)/g, '').trim() : '';
    const display = shortName ? `${t.geneSymbol} — ${shortName}` : t.geneSymbol;
    return `${display} (${structureNote})`;
}

// ─── L1 → workflow: picked target to DMTA plan ────────────────────────────

/**
 * Build a workflow plan (or a nested clarification) once a user has
 * picked a target from the L0 cascade.
 *
 * 2026-06-02: optional `workflowIntent` parameter (passed from the
 * clarification context) lets us emit a multi-step pre-resolved plan
 * (e.g., full hit-to-lead with loadPdbId already queued into step 1)
 * instead of just the acquire step. When absent, falls back to the
 * acquire-only plan — matches the historical behavior where the
 * user re-prompts for "dock and score" or "discover hits" after
 * loading.
 *
 * @param {string} diseaseSlug
 * @param {number} targetIdx
 * @param {string|null} [workflowIntent] - template id like 'hit-to-lead'
 * @returns {Object} ActionPlan-shaped result or clarify-intent
 */
export function buildWorkflowForSelectedTarget(diseaseSlug, targetIdx, workflowIntent = null) {
    const entry = diseaseTargets.diseases?.[diseaseSlug];
    if (!entry) {
        return {
            intent: 'chat',
            message: `I lost track of the disease context for "${diseaseSlug}". `
                + 'Could you restate your request?',
        };
    }

    if (targetIdx === -1) {
        // User said "none of these — I have a different target"
        return {
            intent: 'chat',
            message: 'No problem. Tell me the name or UniProt ID of the target'
                + ' you want to pursue, and I can plan a workflow against it.',
        };
    }

    const target = (entry.topTargets || [])[targetIdx];
    if (!target) {
        return {
            intent: 'chat',
            message: 'That target option was not recognized. Please try again.',
        };
    }

    // Branch on structure availability
    if (target.pdbCount > 0 && target.pdbIds && target.pdbIds[0]) {
        // 2026-06-02: if the user's original message named a workflow
        // template (e.g., "hit to lead"), emit the FULL template plan
        // with loadPdbId pre-filled into step 1 — saves the user from
        // re-prompting after the acquire step completes.
        if (workflowIntent) {
            const fullPlan = buildTemplateWorkflowWithPreFilledPdb(
                entry, target, workflowIntent,
            );
            if (fullPlan) return fullPlan;
        }
        return buildAcquireStructureWorkflowFromPdb(entry, target);
    }

    // No experimental structure — build a clarification asking how to
    // acquire one, rather than silently dropping the load step.
    return buildStructureSourceClarification(diseaseSlug, targetIdx, target);
}

/**
 * Build a full template plan (e.g., hit-to-lead) with the
 * load-protein-from-pdb step's pdbId pre-resolved from the user's
 * disease-cascade target pick. Returns null if the template doesn't
 * exist OR if its first step isn't a load-protein-from-pdb (in which
 * case the caller falls back to the acquire-only plan).
 *
 * @param {Object} diseaseEntry
 * @param {Object} target
 * @param {string} templateId
 * @returns {Object|null}
 */
function buildTemplateWorkflowWithPreFilledPdb(diseaseEntry, target, templateId) {
    const plan = buildPlanFromTemplate(templateId);
    if (!plan || !Array.isArray(plan.actions) || plan.actions.length === 0) {
        return null;
    }
    const firstStep = plan.actions[0];
    if (firstStep.actionId !== 'load-protein-from-pdb') {
        // Template doesn't start with load-protein-from-pdb — we can't
        // pre-fill in a structured way. Fall back to acquire-only.
        return null;
    }
    const pdbId = target.preferredDemoPdb || (target.pdbIds && target.pdbIds[0]);
    if (!pdbId) return null;
    const targetLabel = `${target.geneSymbol || 'the target'} (${diseaseEntry.canonicalName})`;
    // Patch the first step's resolvedInputs to include the chosen pdbId.
    // Per registry contract, load-protein-from-pdb accepts pdbId as a
    // declared input (not a parameter), so it must live in resolvedInputs.
    const patchedActions = plan.actions.map((a, i) => (
        i === 0
            ? {
                ...a,
                resolvedInputs: { ...(a.resolvedInputs || {}), pdbId },
            }
            : a
    ));
    return {
        ...plan,
        actions: patchedActions,
        summary: `${plan.summary}\n\nLoading ${targetLabel} from PDB ${pdbId} as step 1.`,
    };
}

/**
 * Build the acquire-structure workflow for a target with an available
 * PDB entry. Intentionally scoped to just the load-protein-from-pdb
 * step — the scoring cycle (dock → minimize → score) is emitted later
 * by the dock-and-score shortcut, after the user has had a chance to
 * load the compounds they want to work with.
 *
 * Rationale: the previous 6-step workflow assumed BMaps has starter
 * compounds available for every target, which is only true for curated
 * project proteins (kinases etc.), not research PDB entries like HBV
 * capsid 6HTX. Splitting acquisition from scoring avoids that
 * assumption and matches how a chemist actually works — structure
 * first, then bring their compounds, then run the scoring cycle.
 *
 * (Real hit discovery — fragment-based, virtual screening, or de novo —
 * is a separate future cascade branch. See hitDiscoveryShortcut.js
 * for the roadmap.)
 *
 * @param {Object} diseaseEntry
 * @param {Object} target
 * @returns {Object} workflow intent object
 */
function buildAcquireStructureWorkflowFromPdb(diseaseEntry, target) {
    // Prefer a hand-curated demo PDB if the target entry specifies one —
    // this lets the seed data point at a PDB known to have a co-crystallized
    // ligand, so BMaps can auto-detect the binding site on load. Falling
    // back to pdbIds[0] (the highest-resolution Open Targets hit) is
    // fine for targets that haven't been hand-tuned for the demo.
    const pdbId = target.preferredDemoPdb || target.pdbIds[0];
    const targetLabel = `${target.geneSymbol || 'the target'} (${diseaseEntry.canonicalName})`;
    return buildDockAndScoreWorkflow({
        phase: 'acquire',
        loadPdbId: pdbId,
        targetLabel,
    });
}

/**
 * Build a nested clarification for the "no experimental structure" branch.
 * The user picks how they want to acquire a structure (AlphaFold fetch,
 * fold it themselves, or provide their own file). The workflow is
 * built after they answer.
 *
 * @param {string} diseaseSlug
 * @param {number} targetIdx
 * @param {Object} target
 * @returns {Object} clarify-intent object
 */
function buildStructureSourceClarification(diseaseSlug, targetIdx, target) {
    const targetLabel = target.name || target.geneSymbol;
    const question = `${targetLabel} does not have an experimental X-ray or cryo-EM `
        + 'structure in the PDB. How would you like to acquire a structure to '
        + 'work from?';

    const options = [
        {
            label: 'Fold it with Boltz / AlphaFold (runs as a background job)',
            value: {
                cascade: 'disease_target_selection',
                diseaseSlug,
                targetIdx,
                structureSource: 'fold',
            },
        },
        {
            label: 'I will provide a PDB file myself (skip the load step)',
            value: {
                cascade: 'disease_target_selection',
                diseaseSlug,
                targetIdx,
                structureSource: 'manual',
            },
        },
        {
            label: 'Not yet — let me think about this target first',
            value: {
                cascade: 'disease_target_selection',
                diseaseSlug,
                targetIdx,
                structureSource: 'abort',
            },
        },
    ];

    return {
        intent: 'clarify',
        question,
        options,
        missing: ['structureSource'],
        context: {
            cascade: 'disease_target_selection',
            diseaseSlug,
            targetIdx,
            stage: 'L1_to_L2',
        },
    };
}

// ─── L1 → L2 resolution: picked structure source ──────────────────────────

/**
 * Resolve the L1 → L2 clarification answer by building the appropriate
 * workflow plan based on how the user wants to acquire a structure.
 *
 * @param {string} diseaseSlug
 * @param {number} targetIdx
 * @param {string} structureSource - 'fold' | 'manual' | 'abort'
 * @returns {Object} workflow intent, chat intent, or clarify intent
 */
export function buildWorkflowForStructureSource(
    diseaseSlug, targetIdx, structureSource,
) {
    const entry = diseaseTargets.diseases?.[diseaseSlug];
    const target = entry?.topTargets?.[targetIdx];
    if (!entry || !target) {
        return {
            intent: 'chat',
            message: 'I lost track of the target context. Could you restate your request?',
        };
    }

    if (structureSource === 'abort') {
        return {
            intent: 'chat',
            message: `No problem. When you're ready to work on ${target.geneSymbol}, `
                + "let me know and we'll pick up where we left off.",
        };
    }

    if (structureSource === 'fold') {
        // Honest graceful degradation: BMaps supports structure prediction
        // via Boltz/Chai through the Protein Folding tab, but the
        // fold-protein registry action takes a structured foldingInput
        // (sequence + ligands + program config) that Gibbs can't assemble
        // from just a UniProt accession without a runtime UniProt sequence
        // fetch. Rather than emit a workflow that will fail at input
        // resolution, tell the user how to kick it off manually.
        const uniprotLink = `https://www.uniprot.org/uniprotkb/${target.uniprot}/entry`;
        return {
            intent: 'chat',
            message: `To fold ${target.geneSymbol} (UniProt ${target.uniprot}):\n\n`
                + '1. Open the **Protein Folding** tab in the BMaps Import dialog.\n'
                + `2. Paste the UniProt ID \`${target.uniprot}\` or fetch the sequence from ${uniprotLink}\n`
                + '3. Pick a folding program (Boltz-1, Boltz-2, or Chai-1) and submit.\n'
                + '4. Once the predicted structure loads into the workspace, come back here'
                + ' and ask me to **"dock and score"** — I\'ll build the dock →'
                + ' minimize → score workflow against the predicted model.\n\n'
                + '*(Gibbs can plan the fold but can\'t kick it off directly yet — that\'s'
                + ' a planned enhancement. For now, the Protein Folding tab is the way.)*',
        };
    }

    if (structureSource === 'manual') {
        return {
            intent: 'chat',
            message: `Got it. Load your ${target.geneSymbol} structure manually `
                + '(drag-and-drop a PDB file, or use the import dialog). Once '
                + "the protein is in the workspace, ask me to 'dock and score' "
                + "and I'll plan the dock → minimize → score steps for you.",
        };
    }

    return {
        intent: 'chat',
        message: `Unrecognized structure source "${structureSource}". `
            + 'Please try again.',
    };
}
